// src/components/home/VehicleSection.jsx

import React, { useEffect, useState } from "react";
import {
  ArrowRight,
  Car,
  ChevronRight,
  X,
} from "lucide-react";

import SectionTitle from "../common/SectionTitle";
import VehicleCard from "./VehicleCard";

// =====================================================
// LOCAL VEHICLE IMAGES
// =====================================================

import ertigaImage from "../../assets/vehicles/ertiga.png";
import tempoImage from "../../assets/vehicles/van.png";
import coachImage from "../../assets/vehicles/coach.png";
import coachImage2 from "../../assets/vehicles/coach2.png";

// =====================================================
// TEMPORARY LOCAL VEHICLE DATA
//
// Later you can replace this with Supabase data.
// =====================================================

const localVehicles = [
  {
    id: "ertiga",
    name: "Maruti Ertiga",
    vehicle_type: "Ertiga",
    description:
      "Comfortable and spacious vehicle suitable for family trips, airport transfers and local travel.",
    image: ertigaImage,
    passengers: 6,
    luggage: 3,
    status: "Available",
  },

  {
    id: "tempo",
    name: "Tata Marcopolo",
    vehicle_type: "Tempo Traveller",
    description:
      "Spacious traveller for family tours, functions and outstation journeys.",
    image: tempoImage,
    passengers: 12,
    luggage: 6,
    status: "Available",
  },

  {
    id: "coach",
    name: "Tata 407 coach van",
    vehicle_type: "Coach",
    description:
      "Large comfortable coach for group tours, school trips, corporate travel and events.",
    image: coachImage,
    passengers: 35,
    luggage: 15,
    status: "Available",
  },

  {
    id: "coach2",
    name: "Mahindra Tourister",
    vehicle_type: "Coach",
    description:
      "Comfortable coach suitable for large groups, tours and outstation travel.",
    image: coachImage2,
    passengers: 35,
    luggage: 15,
    status: "Available",
  },
];

// =====================================================
// VEHICLE SECTION
// =====================================================

const VehicleSection = ({ onBookVehicle }) => {
  const [vehicles] = useState(localVehicles);

  const [selectedVehicle, setSelectedVehicle] = useState(null);

  // =====================================================
  // OPEN IMAGE MODAL
  // =====================================================

  const handleImageClick = (vehicle) => {
    setSelectedVehicle(vehicle);
  };

  // =====================================================
  // CLOSE IMAGE MODAL
  // =====================================================

  const closeImageModal = () => {
    setSelectedVehicle(null);
  };

  // =====================================================
  // ESC KEY + BODY SCROLL LOCK
  // =====================================================

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        closeImageModal();
      }
    };

    if (selectedVehicle) {
      document.addEventListener("keydown", handleKeyDown);

      // Prevent background scrolling
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.removeEventListener("keydown", handleKeyDown);

      document.body.style.overflow = "";
    };
  }, [selectedVehicle]);

  return (
    <>
      {/* =================================================
          VEHICLE SECTION
      ================================================= */}

      <section
        id="vehicles"
        className="
          relative
          mx-auto
          max-w-7xl
          overflow-hidden
          px-4
          py-12
          sm:px-6
          sm:py-16
          lg:px-8
        "
      >
        {/* =================================================
            DECORATIVE BACKGROUND
        ================================================= */}

        <div
          aria-hidden="true"
          className="
            pointer-events-none
            absolute
            -right-32
            top-20
            h-72
            w-72
            rounded-full
            bg-[#00A260]/5
            blur-3xl
          "
        />

        <div
          aria-hidden="true"
          className="
            pointer-events-none
            absolute
            -left-32
            bottom-0
            h-64
            w-64
            rounded-full
            bg-[#8BF108]/5
            blur-3xl
          "
        />

        <div className="relative">

          {/* =================================================
              SECTION HEADER
          ================================================= */}

          <div
            className="
              flex
              flex-col
              gap-5
              sm:flex-row
              sm:items-end
              sm:justify-between
            "
          >
            <SectionTitle
              eyebrow="Our Fleet"
              title="Vehicles for every journey"
              subtitle="Choose from our comfortable and reliable vehicles for local trips, airport transfers and outstation travel."
            />

            {/* Desktop vehicle count */}
            {vehicles.length > 0 && (
              <div
                className="
                  hidden
                  shrink-0
                  items-center
                  gap-2
                  rounded-full
                  border
                  border-[#E2E8E5]
                  bg-white
                  px-4
                  py-2
                  shadow-sm
                  sm:flex
                "
              >
                <span
                  className="
                    flex
                    h-7
                    w-7
                    items-center
                    justify-center
                    rounded-full
                    bg-[#00A260]/10
                    text-[#00A260]
                  "
                >
                  <Car className="h-3.5 w-3.5" />
                </span>

                <span className="text-xs font-bold text-[#061A3A]">
                  {vehicles.length}{" "}
                  {vehicles.length === 1 ? "Vehicle" : "Vehicles"}
                </span>
              </div>
            )}
          </div>

          {/* =================================================
              MOBILE VEHICLE COUNT
          ================================================= */}

          {vehicles.length > 0 && (
            <div className="mt-4 flex items-center gap-2 sm:hidden">

              <div
                className="
                  flex
                  h-8
                  items-center
                  gap-2
                  rounded-full
                  bg-[#00A260]/5
                  px-3
                  text-xs
                  font-semibold
                  text-[#00A260]
                "
              >
                <Car className="h-3.5 w-3.5" />

                {vehicles.length}{" "}
                {vehicles.length === 1 ? "vehicle" : "vehicles"} available
              </div>

              <div
                className="
                  ml-auto
                  flex
                  items-center
                  gap-1
                  text-[11px]
                  font-medium
                  text-[#94A3B8]
                "
              >
                Swipe

                <ChevronRight className="h-3.5 w-3.5" />
              </div>

            </div>
          )}

          {/* =================================================
              VEHICLE CARDS
          ================================================= */}

          {vehicles.length === 0 ? (
            <div
              className="
                mt-8
                rounded-3xl
                border
                border-dashed
                border-[#E2E8E5]
                bg-[#F8FAF9]
                px-6
                py-12
                text-center
              "
            >
              <div
                className="
                  mx-auto
                  flex
                  h-14
                  w-14
                  items-center
                  justify-center
                  rounded-2xl
                  bg-white
                  text-[#00A260]
                  shadow-sm
                "
              >
                <Car className="h-6 w-6" />
              </div>

              <h3 className="mt-4 text-lg font-bold text-[#061A3A]">
                Vehicles are being updated
              </h3>

              <p
                className="
                  mx-auto
                  mt-2
                  max-w-md
                  text-sm
                  leading-6
                  text-[#64748B]
                "
              >
                Our fleet information is currently being updated.
                Please contact NJV Tours & Travels for vehicle
                availability.
              </p>
            </div>
          ) : (
            <>
              {/* =================================================
                  VEHICLE GRID
              ================================================= */}

              <div
                className="
                  mt-7

                  flex
                  gap-3
                  overflow-x-auto
                  pb-4

                  [scrollbar-width:none]
                  [&::-webkit-scrollbar]:hidden

                  sm:mt-8
                  sm:gap-4

                  md:grid
                  md:grid-cols-2
                  md:gap-5
                  md:overflow-visible

                  lg:grid-cols-3

                  xl:grid-cols-4
                "
              >
                {vehicles.map((vehicle, index) => (
                  <div
                    key={vehicle.id}
                    className="
                      min-w-[225px]
                      max-w-[235px]
                      shrink-0
                      animate-[vehicleReveal_0.6s_ease-out_both]

                      sm:min-w-[260px]
                      sm:max-w-none

                      md:min-w-0
                      md:max-w-none
                    "
                    style={{
                      animationDelay: `${index * 100}ms`,
                    }}
                  >
                    <VehicleCard
                      vehicle={vehicle}
                      image={vehicle.image}
                      onBook={onBookVehicle}
                      onImageClick={() =>
                        handleImageClick(vehicle)
                      }
                    />
                  </div>
                ))}
              </div>

              {/* =================================================
                  MOBILE SWIPE HINT
              ================================================= */}

              {vehicles.length > 1 && (
                <div
                  className="
                    mt-1
                    flex
                    items-center
                    justify-center
                    gap-2
                    text-[11px]
                    font-medium
                    text-[#94A3B8]
                    md:hidden
                  "
                >
                  <span className="h-px w-8 bg-[#E2E8E5]" />

                  Swipe to explore vehicles

                  <span className="h-px w-8 bg-[#E2E8E5]" />
                </div>
              )}

              {/* =================================================
                  BOTTOM CTA
              ================================================= */}

              <div
                className="
                  mt-8
                  flex
                  flex-col
                  gap-4
                  rounded-2xl
                  border
                  border-[#E2E8E5]
                  bg-[#F8FAF9]
                  p-5

                  sm:flex-row
                  sm:items-center
                  sm:justify-between
                  sm:rounded-3xl
                  sm:p-6
                "
              >
                <div>
                  <p className="text-sm font-bold text-[#061A3A]">
                    Need help choosing a vehicle?
                  </p>

                  <p
                    className="
                      mt-1
                      text-xs
                      leading-5
                      text-[#64748B]
                      sm:text-sm
                    "
                  >
                    Tell us about your journey and we'll help you
                    choose a suitable vehicle.
                  </p>
                </div>

                <button
                  type="button"
                  onClick={() => onBookVehicle?.()}
                  className="
                    inline-flex
                    h-11
                    shrink-0
                    items-center
                    justify-center
                    gap-2
                    rounded-xl
                    bg-[#061A3A]
                    px-5
                    text-sm
                    font-bold
                    text-white
                    transition-all
                    duration-300
                    hover:bg-[#0B2A4A]
                    hover:shadow-md
                    active:scale-[0.98]
                  "
                >
                  Find a Vehicle

                  <ArrowRight className="h-4 w-4" />
                </button>
              </div>
            </>
          )}
        </div>

        {/* =================================================
            CARD REVEAL ANIMATION
        ================================================= */}

        <style>{`
          @keyframes vehicleReveal {
            from {
              opacity: 0;
              transform: translateY(18px);
            }

            to {
              opacity: 1;
              transform: translateY(0);
            }
          }

          @media (prefers-reduced-motion: reduce) {
            * {
              animation-duration: 0.01ms !important;
              animation-iteration-count: 1 !important;
              transition-duration: 0.01ms !important;
            }
          }
        `}</style>
      </section>

      {/* =====================================================
          IMAGE PREVIEW MODAL
          IMAGE ONLY
      ===================================================== */}

      {selectedVehicle && (
        <div
          className="
            fixed
            inset-0
            z-[200]
            flex
            items-center
            justify-center
            bg-black/80
            p-3
            backdrop-blur-md
            sm:p-6
          "
          role="dialog"
          aria-modal="true"
          aria-label="Vehicle image preview"
          onClick={closeImageModal}
        >

          {/* =================================================
              CLOSE BUTTON
          ================================================= */}

          <button
            type="button"
            onClick={closeImageModal}
            aria-label="Close image preview"
            className="
              absolute
              right-3
              top-3
              z-20
              flex
              h-10
              w-10
              items-center
              justify-center
              rounded-full
              bg-white
              text-[#061A3A]
              shadow-lg
              transition-all
              duration-200
              hover:bg-[#F1F5F3]
              hover:scale-105
              active:scale-95

              sm:right-5
              sm:top-5
              sm:h-11
              sm:w-11
            "
          >
            <X className="h-5 w-5" />
          </button>

          {/* =================================================
              IMAGE ONLY
          ================================================= */}

          <div
            className="
              flex
              h-auto
              max-h-[90vh]
              w-full
              max-w-5xl
              items-center
              justify-center
            "
            onClick={(event) => event.stopPropagation()}
          >
            <img
              src={selectedVehicle.image}
              alt={selectedVehicle.name}
              className="
                block
                max-h-[88vh]
                w-auto
                max-w-full
                rounded-xl
                object-contain
                shadow-2xl

                sm:max-h-[88vh]
                sm:rounded-2xl
              "
            />
          </div>
        </div>
      )}
    </>
  );
};

export default VehicleSection;